/**
 * GMB Questions Management Page
 * 
 * This page provides a comprehensive interface for managing Google My Business Q&A:
 * - Lists all questions from the gmb_questions table
 * - Shows answered/unanswered status with visual indicators
 * - Filters by location, status, priority, date range
 * - Groups questions by location for easy management
 * - Shows upvote count to identify popular questions
 * - Supports bulk actions and answer templating
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'


// import { Calendar } from '@/components/ui/calendar'
// import {
//   Popover,
//   PopoverContent,
//   PopoverTrigger,
// } from '@/components/ui/popover'
import { QuestionAnswer } from '@/features/gmb/components/qa/QuestionAnswer'
import { publishQuestionAnswer } from '@/utils/gmbPublish'
import { SyncStatusIndicator } from '@/features/gmb/components/shared/SyncStatusWidget'
import { GmbSyncType } from '@/features/gmb/types'
import { 
  MessageSquare,
  Search,
  MapPin,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Copy,
  FileText,
  RefreshCw,
  Sparkles
} from 'lucide-react'
// import { format } from 'date-fns'
import type { GmbQuestion, GmbLocation, AnswerQuestionInput } from '@/types/gmb'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/use-toast'

/**
 * Question with location details
 */
interface QuestionWithLocation extends GmbQuestion {
  location?: GmbLocation
}

type LocationOption = Pick<GmbLocation, 'id' | 'gmb_location_id' | 'location_name' | 'address_city'>

/**
 * Answer template interface
 */
interface AnswerTemplate {
  id: string
  name: string
  template: string
  category: string
}

/**
 * Predefined answer templates for common questions
 */
const ANSWER_TEMPLATES: AnswerTemplate[] = [
  {
    id: '1',
    name: 'Business Hours',
    category: 'Hours',
    template: 'We are open Monday-Friday 8AM-6PM, Saturday 9AM-4PM. We are closed on Sundays. You can also schedule appointments outside regular hours by calling us at {phone}.'
  },
  {
    id: '2',
    name: 'Service Area',
    category: 'Location',
    template: 'Yes, we proudly serve {location} and the surrounding areas within a 50km radius. We offer free estimates for all projects in our service area.'
  },
  {
    id: '3',
    name: 'Pricing Information',
    category: 'Pricing',
    template: 'Pricing varies based on the scope of work, materials needed, and project complexity. We offer free, no-obligation estimates. Please call us at {phone} or fill out our online form to schedule an assessment.'
  },
  {
    id: '4',
    name: 'Emergency Service',
    category: 'Service',
    template: 'Yes, we offer emergency masonry repair services. For urgent issues, please call us directly at {phone}. Emergency service rates may apply outside regular business hours.'
  },
  {
    id: '5',
    name: 'Warranty',
    category: 'Service',
    template: 'We stand behind our work with a comprehensive warranty. All masonry repairs come with a minimum 2-year warranty on workmanship. Extended warranties are available for larger projects.'
  }
]

/**
 * GMB Questions Management Page
 */
export default function GMBQuestions() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  
    // State
    const [questions, setQuestions] = useState<QuestionWithLocation[]>([])
    const [locations, setLocations] = useState<LocationOption[]>([])
    const [loading, setLoading] = useState(true)
    const [syncing, setSyncing] = useState(false)
    const [submitting, setSubmitting] = useState(false)
  
  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || 'all')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all')
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: searchParams.get('from') ? new Date(searchParams.get('from')!) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to')!) : undefined
  })
  
  // Bulk selection
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set())
  const [, setShowBulkAnswer] = useState(false)
  
  /**
   * Load questions from database
   */
  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('gmb_questions')
        .select(`
          *,
          location:gmb_locations(
            id,
            location_name,
            address_city,
            primary_phone
          )
        `)
        .order('is_answered', { ascending: true })
        .order('upvote_count', { ascending: false })
        .order('gmb_created_at', { ascending: false })
      
      if (error) throw error
      
      setQuestions(data as QuestionWithLocation[])
    } catch (error) {
      console.error('Error loading questions:', error)
      toast({
        title: 'Error loading questions',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }, [toast])
  
  /**
   * Load locations for filtering
   */
  const loadLocations = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('gmb_locations')
        .select('id, gmb_location_id, location_name, address_city')
        .eq('is_active', true)
        .order('location_name')
      
        if (error) throw error
        
        setLocations((data || []) as LocationOption[])
      } catch (error) {
        console.error('Error loading locations:', error)
      }
    }, [])

  // Load data
  useEffect(() => {
    void loadQuestions()
    void loadLocations()
  }, [loadQuestions, loadLocations])
  
  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (selectedLocation !== 'all') params.set('location', selectedLocation)
    if (statusFilter !== 'all') params.set('status', statusFilter)
    if (priorityFilter !== 'all') params.set('priority', priorityFilter)
    if (dateRange.from) params.set('from', dateRange.from.toISOString())
    if (dateRange.to) params.set('to', dateRange.to.toISOString())
    setSearchParams(params)
  }, [searchTerm, selectedLocation, statusFilter, priorityFilter, dateRange, setSearchParams])
  
  /**
   * Sync questions from GMB
   */
  async function syncQuestions() {
    try {
      setSyncing(true)
      
      // For now, this is a mock sync
      // In production, this would call the GMB Worker API
      toast({
        title: 'Sync initiated',
        description: 'Questions sync feature coming soon. Using mock data for now.'
      })
      
      // Simulate sync delay
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Add some mock questions for testing
      const mockQuestions = [
        {
          gmb_question_id: 'mock-q-1',
          gmb_location_id: locations[0]?.gmb_location_id || 'mock-location-1',
          author_name: 'John Smith',
          text: 'What are your business hours?',
          is_answered: false,
          upvote_count: 5,
          gmb_created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          synced_at: new Date().toISOString()
        },
        {
          gmb_question_id: 'mock-q-2',
          gmb_location_id: locations[0]?.gmb_location_id || 'mock-location-1',
          author_name: 'Sarah Johnson',
          text: 'Do you offer free estimates for chimney repair?',
          is_answered: true,
          answer_text: 'Yes, we offer free estimates for all our services including chimney repair. Please call us to schedule an appointment.',
          answered_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          upvote_count: 3,
          gmb_created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          synced_at: new Date().toISOString()
        },
        {
          gmb_question_id: 'mock-q-3',
          gmb_location_id: locations[1]?.gmb_location_id || 'mock-location-2',
          author_name: 'Mike Chen',
          text: 'Can you repair brick foundations?',
          is_answered: false,
          upvote_count: 8,
          gmb_created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          synced_at: new Date().toISOString()
        }
      ]
      
      // Insert mock questions if they don't exist
      for (const question of mockQuestions) {
        const { error } = await supabase
          .from('gmb_questions')
          .upsert(question, {
            onConflict: 'gmb_question_id'
          })
        
        if (error) {
          console.error('Error inserting mock question:', error)
        }
      }
      
      toast({
        title: 'Mock sync completed',
        description: `Added ${mockQuestions.length} sample questions for testing`
      })
      
      // Reload questions
      await loadQuestions()
    } catch (error) {
      toast({
        title: 'Sync failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setSyncing(false)
    }
  }
  
  /**
   * Handle answer submission
   */
  async function handleAnswerSubmit(answer: AnswerQuestionInput) {
    try {
      setSubmitting(true)
      
      // Save draft first
      const { data: { user } } = await supabase.auth.getUser()
      await supabase
        .from('gmb_questions')
        .update({
          answer_text: answer.answer_text,
          answered_by: user?.id,
          answered_at: new Date().toISOString()
        })
        .eq('gmb_question_id', answer.gmb_question_id)
      
      // Publish to GMB
      const result = await publishQuestionAnswer({
        questionId: answer.gmb_question_id,
        locationId: answer.gmb_location_id,
        answerText: answer.answer_text
      })
      
      if (result.success) {
        toast({
          title: 'Answer published',
          description: 'Your answer has been published to Google'
        })
        
        // Reload questions
        await loadQuestions()
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      toast({
        title: 'Failed to publish answer',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setSubmitting(false)
    }
  }
  
  /**
   * Filter questions based on current filters
   */
  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        if (!q.text.toLowerCase().includes(search) &&
            !(q.author_name?.toLowerCase().includes(search)) &&
            !(q.answer_text?.toLowerCase().includes(search))) {
          return false
        }
      }
      
      // Location filter
      if (selectedLocation !== 'all' && q.gmb_location_id !== selectedLocation) {
        return false
      }
      
      // Status filter
      if (statusFilter === 'answered' && !q.is_answered) return false
      if (statusFilter === 'unanswered' && q.is_answered) return false
      
      // Priority filter
      if (priorityFilter === 'popular' && q.upvote_count < 5) return false
      if (priorityFilter === 'recent') {
        const daysSinceCreated = (Date.now() - new Date(q.gmb_created_at).getTime()) / (1000 * 60 * 60 * 24)
        if (daysSinceCreated > 7) return false
      }
      
      // Date range filter
      if (dateRange.from || dateRange.to) {
        const questionDate = new Date(q.gmb_created_at)
        if (dateRange.from && questionDate < dateRange.from) return false
        if (dateRange.to && questionDate > dateRange.to) return false
      }
      
      return true
    })
  }, [questions, searchTerm, selectedLocation, statusFilter, priorityFilter, dateRange])
  
  /**
   * Group questions by location
   */
  const questionsByLocation = useMemo(() => {
    const grouped = new Map<string, QuestionWithLocation[]>()
    
    filteredQuestions.forEach(q => {
      const locationName = q.location?.location_name || 'Unknown Location'
      if (!grouped.has(locationName)) {
        grouped.set(locationName, [])
      }
      grouped.get(locationName)!.push(q)
    })
    
    return Array.from(grouped.entries()).sort((a, b) => {
      // Sort by number of unanswered questions first
      const unansweredA = a[1].filter(q => !q.is_answered).length
      const unansweredB = b[1].filter(q => !q.is_answered).length
      if (unansweredA !== unansweredB) return unansweredB - unansweredA
      
      // Then by total questions
      return b[1].length - a[1].length
    })
  }, [filteredQuestions])
  
  /**
   * Find duplicate questions across locations
   */
  const duplicateQuestions = useMemo(() => {
    const questionMap = new Map<string, QuestionWithLocation[]>()
    
    questions.forEach(q => {
      if (!q.is_answered) {
        const normalizedText = q.text.toLowerCase().trim().replace(/[?!.,]/g, '')
        if (!questionMap.has(normalizedText)) {
          questionMap.set(normalizedText, [])
        }
        questionMap.get(normalizedText)!.push(q)
      }
    })
    
    return Array.from(questionMap.entries())
      .filter(([_, qs]) => qs.length > 1)
      .sort((a, b) => b[1].length - a[1].length)
  }, [questions])
  
  // Statistics
  const stats = useMemo(() => {
    const total = questions.length
    const answered = questions.filter(q => q.is_answered).length
    const unanswered = total - answered
    const popular = questions.filter(q => q.upvote_count >= 5).length
    const recent = questions.filter(q => {
      const daysSinceCreated = (Date.now() - new Date(q.gmb_created_at).getTime()) / (1000 * 60 * 60 * 24)
      return daysSinceCreated <= 7
    }).length
    
    return { total, answered, unanswered, popular, recent }
  }, [questions])
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }
  
  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">GMB Questions & Answers</h1>
          <p className="text-muted-foreground">
            Manage customer questions across all locations
            </p>
            <div className="mt-2">
              <SyncStatusIndicator syncType={GmbSyncType.QUESTIONS} />
            </div>
          </div>
        <Button onClick={() => { void syncQuestions(); }} disabled={syncing}>
          <RefreshCw className={cn("h-4 w-4 mr-2", syncing && "animate-spin")} />
          Sync Questions
        </Button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Answered
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.answered}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Needs Answer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.unanswered}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Popular (5+ votes)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.popular}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Recent (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.recent}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Alerts */}
      {stats.unanswered > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You have {stats.unanswered} unanswered question{stats.unanswered !== 1 ? 's' : ''}.
            Answering questions helps improve your local search ranking and builds trust with potential customers.
          </AlertDescription>
        </Alert>
      )}
      
      {duplicateQuestions.length > 0 && (
        <Alert>
          <Copy className="h-4 w-4" />
          <AlertDescription>
            Found {duplicateQuestions.length} similar questions across multiple locations.
            Consider using bulk answers to respond efficiently.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            {/* Location Filter */}
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-[200px]">
                <MapPin className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(location => (
                  <SelectItem key={location.id} value={location.gmb_location_id}>
                    {location.location_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="unanswered">Unanswered</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Priority Filter */}
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="popular">Popular (5+ votes)</SelectItem>
                <SelectItem value="recent">Recent (7 days)</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Date Range - Temporarily disabled until Calendar component is available */}
            {/* <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[240px] justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} -{" "}
                        {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={{ from: dateRange.from, to: dateRange.to }}
                  onSelect={(range: any) => setDateRange(range || { from: undefined, to: undefined })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover> */}
          </div>
          
          {/* Quick Filters */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatusFilter('unanswered')
                setPriorityFilter('all')
              }}
              className={statusFilter === 'unanswered' ? 'bg-red-50 border-red-200' : ''}
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              Needs Answer ({stats.unanswered})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatusFilter('unanswered')
                setPriorityFilter('popular')
              }}
            >
              <TrendingUp className="h-4 w-4 mr-1" />
              Popular Unanswered
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatusFilter('all')
                setPriorityFilter('recent')
              }}
            >
              <Clock className="h-4 w-4 mr-1" />
              Recent Questions
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('')
                setSelectedLocation('all')
                setStatusFilter('all')
                setPriorityFilter('all')
                setDateRange({ from: undefined, to: undefined })
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabs for different views */}
      <Tabs defaultValue="by-location" className="space-y-4">
        <TabsList>
          <TabsTrigger value="by-location">By Location</TabsTrigger>
          <TabsTrigger value="all-questions">All Questions</TabsTrigger>
          <TabsTrigger value="duplicates">Duplicate Questions</TabsTrigger>
          <TabsTrigger value="templates">Answer Templates</TabsTrigger>
        </TabsList>
        
        <TabsContent value="by-location" className="space-y-6">
          {questionsByLocation.length > 0 ? (
            questionsByLocation.map(([locationName, locationQuestions]) => {
              const unansweredCount = locationQuestions.filter(q => !q.is_answered).length
              
              return (
                <Card key={locationName}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{locationName}</CardTitle>
                        <CardDescription>
                          {locationQuestions.length} question{locationQuestions.length !== 1 ? 's' : ''}
                          {unansweredCount > 0 && (
                            <> • <span className="text-red-600">{unansweredCount} unanswered</span></>
                          )}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={unansweredCount > 0 ? 'destructive' : 'secondary'}>
                          {unansweredCount > 0 ? `${unansweredCount} Needs Answer` : 'All Answered'}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <QuestionAnswer
                      questions={locationQuestions}
                      onSubmit={handleAnswerSubmit}
                      isSubmitting={submitting}
                      enableAISuggestions={false}
                      showOnlyUnanswered={false}
                    />
                  </CardContent>
                </Card>
              )
            })
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No questions match your filters
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="all-questions">
          <Card>
            <CardHeader>
              <CardTitle>All Questions</CardTitle>
              <CardDescription>
                View and manage all questions in one place
              </CardDescription>
            </CardHeader>
            <CardContent>
              <QuestionAnswer
                questions={filteredQuestions}
                onSubmit={handleAnswerSubmit}
                isSubmitting={submitting}
                enableAISuggestions={false}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="duplicates" className="space-y-4">
          <Alert>
            <Sparkles className="h-4 w-4" />
            <AlertDescription>
              These similar questions appear across multiple locations.
              You can use the same answer for all of them to save time.
            </AlertDescription>
          </Alert>
          
          {duplicateQuestions.length > 0 ? (
            duplicateQuestions.map(([questionText, questions], index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base">{questionText}</CardTitle>
                      <CardDescription>
                        Asked at {questions.length} locations
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Select all these questions for bulk answer
                        const newSelection = new Set(selectedQuestions)
                        questions.forEach(q => newSelection.add(q.id))
                        setSelectedQuestions(newSelection)
                        setShowBulkAnswer(true)
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Answer All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {questions.map(q => (
                      <div key={q.id} className="flex items-center justify-between text-sm">
                        <span className="font-medium">{q.location?.location_name}</span>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>{q.author_name || 'Anonymous'}</span>
                          {q.upvote_count > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {q.upvote_count} votes
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No duplicate unanswered questions found
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="templates" className="space-y-4">
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              Use these templates as starting points for common questions.
              Remember to personalize them with specific details.
            </AlertDescription>
          </Alert>
          
          <div className="grid gap-4">
            {ANSWER_TEMPLATES.map(template => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <Badge variant="outline" className="mt-1">{template.category}</Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(template.template)
                        toast({
                          title: 'Template copied',
                          description: 'The template has been copied to your clipboard'
                        })
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {template.template}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
